import { AudioController } from "./AudioController.ts";
import { NonPlayerCharacterController } from './NonPlayerCharacterController.ts';
import levels from "../assets/levels.js";
import quests from "../assets/quests.js";
import settings from "../assets/settings.js";
import GameInteractionController from "./GameInteractionController.ts";
import PlayerController from "./PlayerController.ts";
import { elements } from "../assets/elements.js";
import EventController from "./EventController.ts";

type Settings = {
    debug: boolean,
    items: {
        renderType: string
    },
    gui: {
        player: boolean,
        map: boolean,
        inventory: boolean,
        courses: boolean,
        sleep: boolean,
        quest: boolean,
        discussion: boolean,
        hint: boolean,
        computer: boolean,
        shop: boolean,
        letter: boolean,
        book: boolean,
        spaceinvader: boolean,
    }
}

export type GameState = {
    previousScene: string | null;
    status: "menu" | "loading" | "playing" | "paused" | "finished" | "game-over";
    sceneKey: string | null;
    currentScene: any;
    sceneState: any;
    currentDiscussion: any,
    currentComputerSession: any,
    currentQuest: any,
    characters: any,
    currentCharacter: any,
    currentPlayerHint?: string | null,
    currentPlayerInteraction?: any,
};

type EventType = 'game:updated' | 'quest:completed' | 'scene:updated' | 'scene:loaded' | 'player:interacted' | 'currentPlayerInteraction:updated' | 'game:status:updated' | 'task:completed';
type EventHandlerFunction = (event: EventType, game: GameState) => boolean | void;
export type EventHandler = {
    key: string,
    priority?: number, // Some event needs to be handled before others, like looting
    handler: EventHandlerFunction
};
type EventHandlerDictionary = {[key in EventType]: EventHandler[]};

export class GameController {

    audio: AudioController;
    interaction: GameInteractionController
    levels: any = levels;
    player: PlayerController;
    settings: Settings;
    events: EventController;

    eventHandlers: EventHandlerDictionary = {
        "game:updated": [],
        "quest:completed": [],
        "scene:updated": [],
        "scene:loaded": [],
        "player:interacted": [],
        "currentPlayerInteraction:updated": [],
        "game:status:updated": [],
        "task:completed": []
    };
    gameState: GameState = {
        previousScene: null,
        status: "menu",
        sceneKey: null,
        currentScene: null,
        sceneState: {
            hasComputer: false
        },
        currentDiscussion: null,
        currentComputerSession: null,
        currentQuest: null,
        characters: {
            developer: new NonPlayerCharacterController('developer'),
            boss: new NonPlayerCharacterController('boss'),
            teacher: new NonPlayerCharacterController('teacher'),
            salesman: new NonPlayerCharacterController('salesman'),
            clerk: new NonPlayerCharacterController('clerk'),
            woman: new NonPlayerCharacterController('woman')
        },
        currentPlayerHint: null,
        currentPlayerInteraction: null,
        currentCharacter: null,
    };

    constructor() {
        this.gameState.currentScene = levels[this.gameState.sceneKey];
        this.audio = new AudioController();
        //If new game
        this.gameState.currentQuest = quests[0];
        this.interaction = new GameInteractionController(this);
        this.player = new PlayerController(this);
        this.events = new EventController(this);
        this.settings = settings;

        this.events.setGlobalEvents(levels.events || []);
    }

    addEventHandler(events: string[], listenerKey: string, eventHandler: EventHandlerFunction, priority?: number){
        events.forEach((event) => {
            if (!this.eventHandlers[event]) {
                this.eventHandlers[event] = [];
            }
            const alreadyExists = this.eventHandlers[event].find((listener) => listenerKey === listener.key);
            if(alreadyExists) return;
            this.eventHandlers[event].push({key: listenerKey, handler: eventHandler, priority: priority ?? 0} as EventHandler);
            this.eventHandlers[event] = this.eventHandlers[event].sort((a, b) => b.priority - a.priority);
        });
    }

    removeEventHandler(events: string[], listenerKey: string, eventHandler: EventHandlerFunction){
        events.forEach((event) => {
            if (!this.eventHandlers[event]) return;
            this.eventHandlers[event] = this.eventHandlers[event].filter((listener) => { return listener.key != listenerKey });
            this.eventHandlers[event] = this.eventHandlers[event].sort((a, b) => b.priority - a.priority);
        });
    }

    remoteAllEventHandlersForType(event) {
        if (!this.eventHandlers[event]) return;
        this.eventHandlers[event] = [];
    }

    sendEvent(event: string, data?: any){
        if(!this.eventHandlers[event] && !this.eventHandlers["*"]) return;
        // Some helps to break the loop if a handler returns true
        this.eventHandlers[event].some((listener) => {
            return listener.handler(event, data ?? this.gameState);
        });
        this.eventHandlers["*"]?.some((listener) => {
            return listener.handler(event, data ?? this.gameState);
        });
    };

    loadScene(key: string) {
        try {
            const previousSceneKey = this.gameState.sceneKey+"";
            const previousScene = levels[previousSceneKey];
            this.gameState.previousScene = previousSceneKey;
            this.gameState.sceneKey = key;
            this.gameState.currentScene = levels[key];
            console.log("LOAD EVENTS", this.gameState.currentScene.events);
            this.events.setSceneEvents(this.gameState.currentScene.events || []);
            //this.gameState.sceneState = {}; // Why is this here?
            this.setSceneState(key + ":travelled");
            this.remoteAllEventHandlersForType("player:interacted");
            this.clearPlayerHint();
            document.querySelector("#game-canvas").style.backgroundColor = this.gameState.currentScene.scene.backgroundColor;
            this.sendEvent("scene:loaded");
            console.log("ITEMS", this.gameState.currentScene.elements);
            //Stop previous music
            if(previousScene && previousScene.scene.backgroundMusic){
            this.audio.pauseAudio(previousScene.scene.backgroundMusic);
            }
            if(previousScene && previousScene.scene.ambianceMusic){
                this.audio.pauseAudio(previousScene.scene.ambianceMusic);
            }

            this.audio.playAudio(this.gameState.currentScene.scene.backgroundMusic, 0.1, 1, true);
            if(this.gameState.currentScene.scene.ambianceMusic){
                this.audio.playAudio(this.gameState.currentScene.scene.ambianceMusic, 0.2, 1, true);
            }
            
        } catch (e) {
            console.error(e);
        }
    }

    addElementToScene(key: string, position: number[]) {
        const element = elements[key];
        //const pos = this.player.reference.nextTranslation();
        this.gameState.currentScene.elements.push({
            ...element,
            // [TO-DO] : Position behind the player + some random
            position: position//[pos.x - 0.5, pos.y, pos.z - 0.5]
        });
        this.sendEvent("scene:updated");
    }

    startDiscussion(npcId: string){
        if(this.gameState.characters[npcId]){
            this.audio.playAudio('conversation',1, 1.5);
            setTimeout(() => {
                this.gameState.currentDiscussion = this.gameState.characters[npcId].startDiscussion();
                this.gameState.currentCharacter = this.gameState.characters[npcId];
                if(this.gameState.currentDiscussion.sceneState){
                    this.setSceneState(this.gameState.currentDiscussion.sceneState);
                    this.validateQuestTaskBySceneState(this.gameState.currentDiscussion.sceneState);
                }
                this.sendEvent("game:updated");
            }, 500);
        }
    }

    leaveDiscussion() {
        this.gameState.currentDiscussion = null;
        this.gameState.currentCharacter = null;
        this.sendEvent("game:updated");
    }

    setCurrentPlayerInteraction(type: string, data: any) {
        this.gameState.currentPlayerInteraction = {
            type,
            data
        };
        this.sendEvent("currentPlayerInteraction:updated");
    }

    answerDiscussion(optionIndex: number) {
        const next = this.gameState.currentDiscussion.options[optionIndex].next;
        //Validates on Player answer
        if (this.gameState.currentDiscussion.options[optionIndex].sceneState) {
            this.setSceneState(this.gameState.currentDiscussion.options[optionIndex].sceneState);
            this.validateQuestTaskBySceneState(this.gameState.currentDiscussion.options[optionIndex].sceneState);
        }
        if (this.gameState.currentDiscussion.options[optionIndex].interaction) {
            this.setCurrentPlayerInteraction(this.gameState.currentDiscussion.options[optionIndex].interaction.type, this.gameState.currentDiscussion.options[optionIndex].interaction.data);
            this.leaveDiscussion();
            return;
        }
        if (next === null) {
            this.leaveDiscussion();
        } else {
            const discussion = this.gameState.currentCharacter.nextDiscussion(next);
            this.gameState.currentDiscussion = discussion;
            //Validates on NPC answer
            if (this.gameState.currentDiscussion.sceneState) {
                this.setSceneState(this.gameState.currentDiscussion.sceneState);
                this.validateQuestTaskBySceneState(this.gameState.currentDiscussion.sceneState);
            }
        }
        this.sendEvent("game:updated");
    }

    validateQuestTaskBySceneState(sceneStateKey: string) {
        const task = this.gameState.currentQuest.tasks.find((task) => task.sceneState === sceneStateKey);
        if (task) {
            this.validateQuestTaskByTitle(task.title);
        }
    }

    validateQuestTaskByTitle(taskTitle: string) {
        const task = this.gameState.currentQuest.tasks.find((task) => task.title === taskTitle);
        if (task) {
            task.completed = true;
            this.tryValidateQuest();
            //this.sendEvent("game:updated");
            this.sendEvent("task:completed", task);
        }
    }

    tryValidateQuest() {
        const completed = this.gameState.currentQuest.tasks.every((task) => task.completed);
        if (completed) {
            const nextQuest = this.gameState.currentQuest.next;
            this.player.addExperience(this.gameState.currentQuest.experience);
            this.gameState.currentQuest = quests.find((quest) => quest.key === nextQuest);
            this.sendEvent("game:updated");
            this.sendEvent("quest:completed");
        }
    }

    startComputerSession() {
        this.gameState.currentComputerSession = {};
        this.sendEvent("game:updated");
    }

    leaveComputerSession() {
        this.gameState.currentComputerSession = null;
        this.sendEvent("game:updated");
    }

    getGameState() {
        return this.gameState;
    }

    getAudioController() {
        return this.audio;
    }

    setSceneState(stateKey: string) {
        this.gameState.sceneState[stateKey] = true;
        console.log("Scene State", this.gameState.sceneState);
        try {
            this.validateQuestTaskBySceneState(stateKey);
        } catch (e) {
            console.error(e);

        } finally {
            this.sendEvent("game:updated");
            this.sendEvent("scene:updated");
        }
    }

    setPlayerHint(hint: string) {
        this.gameState.currentPlayerHint = hint;
        this.sendEvent("game:updated");
    }

    clearPlayerHint() {
        this.gameState.currentPlayerHint = null;
        this.sendEvent("game:updated");
    }

    setGameStatus(status: GameState["status"]) {
        this.gameState.status = status;
        this.sendEvent("game:status:updated");
        if(status === "playing"){
            this.player.start();
        }
    }

    takeItem(uid, item: any) {
        const {id, data} = item
        this.setSceneState(data.sceneState);
        this.player.inventory[uid] = item;
        if(id === 'enveloppe'){
            this.setCurrentPlayerInteraction("letter", data.content);
            this.audio.playAudio('enveloppe',0.5, 1.5);
        } else if(id === 'book'){
            this.setCurrentPlayerInteraction("book", data.content);
            this.audio.playAudio('book',0.5);
        }else if(id === 'money'){
            this.player.addMoney(data.amount);
        } else if(id === 'soda'){
            this.player.drink();
        } else if(id === 'snack'){
            this.player.eat();
        } else if(id === 'hotdog'){
            this.player.eat();
        } else {
            this.audio.playAudio('loot',0.2);
        }
        this.gameState.currentScene.elements = this.gameState.currentScene.elements.filter((element) => element.uid !== uid);
        console.log("INVENTORY", this.player.inventory);
        this.player.onInventoryUpdated();
        this.sendEvent("inventory:updated");
    }

    gameOver(){
        if(this.gameState.status === "game-over") return;
        this.setGameStatus("game-over");
        this.audio.pauseAudio(this.gameState.currentScene.scene.backgroundMusic);
        this.audio.pauseAudio(this.gameState.currentScene.scene.ambianceMusic);
        this.audio.playAudio('voice_game_over',0.5);
        
    }

}
